import { Button, Modal } from 'antd';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import useApiQuery from '@/hooks/common/useApiQuery';

const CHECK_INTERVAL = 3 * 60 * 1000;
const VERSION_FILE_PATH = '/version.json';

interface VersionResponse {
    version?: string
}

// Define the fetcher function for Query
const fetcher = async (url: string): Promise<VersionResponse> => {
    const response = await fetch(`${url}?t=${Date.now()}`, {
        cache: 'no-cache', // Ensure fresh data
    });
    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
};

export function VersionCheckModal() {
    const { t } = useTranslation()
    const initialVersionRef = useRef<string | null>(null);

    const { data, error } = useApiQuery<VersionResponse>(VERSION_FILE_PATH, fetcher, {
        refetchInterval: CHECK_INTERVAL,
        refetchOnWindowFocus: true,
        refetchOnReconnect: false,
        retry: true, // Retry on fetch errors
        retryDelay: 5000, // Retry every 5 seconds on error
    });

    if (error) {
        console.error('Failed to fetch version:', error);
    }
    if (initialVersionRef.current === null && data?.version) {
        initialVersionRef.current = data.version;
    }
    const isModalVisible = !!data?.version && initialVersionRef.current !== data.version;

    // Handle refresh button click event
    const handleRefresh = () => {
        window.location.reload();
    };

    // Render Modal only if needed
    if (!isModalVisible) {
        return null; // Don't render anything if the modal shouldn't be visible
    }

    return (
        <Modal
            title={t('发现新版本')}
            visible={isModalVisible}
            closable={false} // Prevent closing via 'x' button
            maskClosable={false} // Prevent closing by clicking the mask
            footer={[
                <Button key="refresh" type="primary" onClick={handleRefresh}>
                    {t('立即更新')}
                </Button>,
            ]}
        // Optional: Customize zIndex if needed to ensure it's above other elements
        // zIndex={1050} // Example zIndex
        >
            <p>{t('系统已更新，请刷新页面。')}</p>
            {/* You could display the initial version if needed */}
            {/* <p>Current installed version: {initialVersionRef.current}</p> */}
        </Modal>
    );
}
